package com.neurobs.ccabserver.transcriptreviewadmin.components.TranscriptMetricsView

import com.neurobs.ccab.casr.TranscriptMetrics
import com.neurobs.ccab.db.StudyData
import com.neurobs.ccabserver.transcriptreviewadmin.states.TranscriptMetricsStateGenerator
import com.neurobs.js.ccab.CASRServer
import com.neurobs.js.util.ServerResponseUtil
import com.neurobs.react.{ReactElementFactory, ReactUtil}
import com.neurobs.react.semanticui.{UiCardFactory, UiContainerFactory, UiDividerFactory, UiLoaderFactory, UiMessageFactory}
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.Hooks.{useEffect, useState}
import slinky.core.facade.ReactElement

import scala.scalajs.js

@react
object TranscriptMetricsView {
  case class Props(enclosingElement: (ReactElement*) => ReactElement)
  val component = FunctionalComponent[Props] { props =>

    val transcriptMetricsState = TranscriptMetricsStateGenerator.accessState

    def segmentStyle = Seq[(String, js.Any)](("minHeight", "100px"))

    def LoadingStudiesElement = () => props.enclosingElement(
      Seq(
        UiContainerFactory.Segment(
          UiLoaderFactory.ActiveTextLoader("Loading CCAB studies...")("mini")
        )(("fluid", segmentStyle))
      )
    )

    def ErrorStudiesElement = (error: String) => props.enclosingElement(
      Seq(
        UiMessageFactory.BasicMessage("The following error occurred while loading studies from the server: " + error)
      )
    )

    def WithStudiesElement = (studies: List[StudyData]) => props.enclosingElement(
      Seq(
        StudyTranscriptMetrics(studies)
      )
    )

    transcriptMetricsState.studiesState.ConditionalElements(
      LoadingElement = LoadingStudiesElement,
      ErrorElement = ErrorStudiesElement,
      DataElement = WithStudiesElement
    )

  }
}

@react
object StudyTranscriptMetrics {
  case class Props(studies: List[StudyData])
  val component = FunctionalComponent[Props] { props =>

    UiCardFactory.CardGroup(
      props.studies.map(StudyTranscriptMetricCard(_))
    )

  }
}

@react
object StudyTranscriptMetricCard {
  case class Props(study: StudyData)
  val component = FunctionalComponent[Props] { props => {

    val (metrics, setMetrics) = useState[Option[Either[String, TranscriptMetrics]]](None)

    def study = props.study

    def loadServerTranscriptMetrics() = {
      CASRServer.getTranscriptMetricsForStudy(study.id)(handleServerTranscriptMetrics)
    }

    def handleServerTranscriptMetrics = ServerResponseUtil.handleObjectResultResponse[TranscriptMetrics](
      handleObject = metrics => {
        setMetrics(Some(Right(metrics)))
      },
      onAnyError = error => {
        setMetrics(Some(Left(error)))
      }
    )

    useEffect(() => {
      loadServerTranscriptMetrics()
    }, Vector())


    metrics match {
      case None => null
      case Some(metricEither) =>
        if (metricEither.map(_.testTranscriptMetrics.totalCount == 0).getOrElse(false)) null
        else {
          UiCardFactory.Card(
            UiCardFactory.Content(
              UiCardFactory.TextHeader(study.displayName),
              UiCardFactory.TextMeta(s"Database id: ${study.id}"),
              metricEither match {
                case Left(error) => UiMessageFactory.BasicErrorMessage(s"There was an error loading transcript data: $error")
                case Right(metrics) => TranscriptMetricsDescription(metrics)
              }
            )
          )
        }
    }
  }}
}


@react
object TranscriptMetricsDescription {
  case class Props(transcriptMetrics: TranscriptMetrics)
  val component = FunctionalComponent[Props] { props =>

    def testTranscriptMetrics = props.transcriptMetrics.testTranscriptMetrics
    def reviewedTranscriptMetrics = props.transcriptMetrics.reviewedTestTranscriptMetrics

    if (testTranscriptMetrics.totalCount == 0) {
      UiCardFactory.Description("No test transcripts for this study")
    } else {
      UiCardFactory.Description(
        ReactElementFactory.P(s"Test transcript count: ${testTranscriptMetrics.totalCount}"),
        ReactElementFactory.P(s"Test transcript error count: ${testTranscriptMetrics.errorCount}"),
        ReactElementFactory.P(s"Awaiting s3 sync: ${testTranscriptMetrics.awaitingSyncCount}"),
        UiDividerFactory.Horizontal,
        ReactElementFactory.P(s"Reviewed transcript count: ${reviewedTranscriptMetrics.totalCount}"),
        ReactElementFactory.P(s"Awaiting s3 sync: ${reviewedTranscriptMetrics.awaitingSyncCount}")
      )
    }
  }
}